/**
 * My Learning fixtures in the **API's** shape (`UserLessonItem` /
 * `MyLearningFolderItem`), as `GET /lessons` returns them.
 *
 * This is deliberately NOT `@/data/demo/mockMyLearningData`. That module holds
 * `LearningItem` — the frontend's own domain model for mock mode, with
 * `progress`, `instructor`, `thumbnail` and relative `Date` objects. The two
 * describe the same screen from opposite sides of the network boundary, and
 * only this one can back an MSW handler.
 *
 * The default `libraryLessons` / `libraryFolders` deliberately match what
 * `myLearningHandler` served when its rows were hand-written inline, so the
 * story assertions that predate this module keep passing unchanged.
 */
import type { LessonError } from '@/types/errors';
import type { MyLearningFolderItem, UserLessonItem } from '@/types/api';

/**
 * One lesson row. `status: 'completed'` is what makes `LearningCard` render
 * its finished state rather than the "Creating lesson…" shimmer.
 */
export function libraryLesson(
  overrides: Partial<UserLessonItem> = {}
): UserLessonItem {
  return {
    lesson_id: 'sb-lesson',
    title: 'Introduction to Machine Learning',
    description:
      'Supervised vs unsupervised learning, and where gradient descent fits.',
    tags: ['ml', 'foundations'],
    author: 'Ada Lovelace',
    source: 'prompt',
    status: 'completed',
    error: null,
    folder_id: null,
    created_at: '2026-01-02T09:00:00Z',
    updated_at: '2026-01-14T16:20:00Z',
    current_version: 'v1',
    ...overrides,
  };
}

/** One folder row, as it appears inside the unified `items` stream. */
export function libraryFolder(
  overrides: Partial<MyLearningFolderItem> = {}
): MyLearningFolderItem {
  return {
    folder_id: 'sb-folder-1',
    name: 'Exam prep',
    parent_folder_id: null,
    tags: ['revision'],
    lesson_count: 4,
    subfolder_count: 1,
    created_at: '2026-01-01T09:00:00Z',
    updated_at: '2026-01-15T09:00:00Z',
    ...overrides,
  };
}

/**
 * A backend failure envelope. `code` is the load-bearing field: `describeFailure`
 * in `@/lib/lesson-error` maps it to the title and body a failed `LearningCard`
 * renders, and an unknown code silently falls through to generic copy — so a
 * story that wants to prove specific failure copy has to pass a code that
 * module actually knows.
 */
export function lessonFailure(
  overrides: Partial<LessonError> = {}
): LessonError {
  return {
    code: 'OVERVIEW_GENERATION_FAILED',
    message: 'LLM returned an empty overview after 3 attempts',
    timestamp: '2026-01-14T16:22:00Z',
    ...overrides,
  };
}

/**
 * The default library: three lessons spanning the three card phases the grid
 * can show — two terminal, one mid-generation.
 *
 * Ordered newest-first, which is the order `GET /lessons` returns and the order
 * the grid renders. `myLearningHandler` re-sorts anyway, so a story adding a row
 * does not have to place it correctly.
 */
export const libraryLessons: UserLessonItem[] = [
  libraryLesson(),
  libraryLesson({
    lesson_id: 'sb-lesson-2',
    title: 'Photosynthesis',
    description: 'Light-dependent reactions and the Calvin cycle.',
    tags: ['biology'],
    updated_at: '2026-01-13T11:05:00Z',
  }),
  libraryLesson({
    lesson_id: 'sb-lesson-3',
    title: 'The Fourier Transform',
    description: 'From the series to the transform, without the hand-waving.',
    tags: ['maths', 'signals'],
    status: 'processing',
    updated_at: '2026-01-12T08:40:00Z',
  }),
];

/** The default folder set — one root folder, interleaved by `updated_at`. */
export const libraryFolders: MyLearningFolderItem[] = [libraryFolder()];

/**
 * A lesson whose generation died before any overview existed — `LearningCard`'s
 * failure phase: mapped failure copy plus an inline retry.
 *
 * The default title and empty description are load-bearing, not laziness.
 * `LearningCard` derives its phase from `hasOverview`, not from `status`: a
 * failed lesson that DOES have a title or description renders as a normal card
 * with retry demoted into the kebab menu (the later-step-failure case). Give
 * this row a real title and the fixture silently stops expressing the state it
 * is named for.
 */
export const failedLibraryLesson: UserLessonItem = libraryLesson({
  lesson_id: 'sb-lesson-failed',
  title: 'My Lesson',
  description: '',
  tags: [],
  status: 'failed',
  error: lessonFailure(),
  updated_at: '2026-01-11T07:15:00Z',
});

/**
 * A lesson that failed a LATER step, after the overview landed. Keeps its
 * content and its CTA; retry lives in the kebab menu.
 */
export const lateFailureLibraryLesson: UserLessonItem = libraryLesson({
  lesson_id: 'sb-lesson-late-failure',
  title: 'Quantum Error Correction',
  description: 'Surface codes and the threshold theorem.',
  tags: ['physics'],
  status: 'failed',
  error: lessonFailure({ code: 'MODEL_ERROR' }),
  updated_at: '2026-01-11T07:15:00Z',
});

/**
 * A lesson still being generated. `status: 'pending'` is what arms the page's
 * 3-second `refetchInterval` — the poll stops only once every lesson on the
 * page is terminal, so a story that includes this row and never resolves it
 * polls for as long as it is mounted.
 */
export const generatingLibraryLesson: UserLessonItem = libraryLesson({
  lesson_id: 'sb-lesson-generating',
  title: 'My Lesson',
  description: '',
  tags: [],
  status: 'pending',
  updated_at: '2026-01-16T10:00:00Z',
});

/**
 * `count` synthetic lessons, for pagination. The page requests `limit: 12`, so
 * anything above 12 produces a second page and the pagination footer.
 *
 * Titles are numbered rather than realistic on purpose: a pagination story
 * asserts *which page* it is on, and "Sample lesson 13" is a far clearer gate
 * than a plausible title the reader has to cross-reference against a fixture.
 */
export function largeLibrary(count: number): UserLessonItem[] {
  return Array.from({ length: count }, (_, i) =>
    libraryLesson({
      lesson_id: `sb-bulk-${i + 1}`,
      title: `Sample lesson ${i + 1}`,
      description: `Row ${i + 1} of ${count} in the paginated library.`,
      tags: i % 2 === 0 ? ['ml'] : ['biology'],
      // Descending, so row 1 is newest and lands first on page 1.
      updated_at: new Date(Date.UTC(2026, 0, 20, 12, 0, 0) - i * 3_600_000)
        .toISOString()
        .replace('.000Z', 'Z'),
    })
  );
}

/** The distinct tags across `libraryLessons` + `libraryFolders`, with counts. */
export const libraryTagOptions = [
  { value: 'ml', count: 1 },
  { value: 'foundations', count: 1 },
  { value: 'biology', count: 1 },
  { value: 'maths', count: 1 },
  { value: 'signals', count: 1 },
  { value: 'revision', count: 1 },
];
